/**
 * Master Counterparty Registry — List page (P3-10).
 *
 * Unified table of all master counterparties with type filters,
 * search, and navigation to detail / new.
 *
 * Route: /admin/counterparties
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Search, Users, ChevronRight, Building2,
  Briefcase, Sparkles, Target, Handshake,
} from 'lucide-react';

import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  supplier: { label: 'Supplier', color: 'bg-blue-100 text-blue-800', icon: Briefcase },
  client: { label: 'Client', color: 'bg-emerald-100 text-emerald-800', icon: Users },
  partner: { label: 'Partner', color: 'bg-amber-100 text-amber-800', icon: Sparkles },
  lead: { label: 'Lead', color: 'bg-red-100 text-red-800', icon: Target },
  jv_partner: { label: 'JV Partner', color: 'bg-purple-100 text-purple-800', icon: Handshake },
  customer: { label: 'Customer', color: 'bg-violet-100 text-violet-800', icon: Building2 },
  contact: { label: 'Contact', color: 'bg-slate-100 text-slate-800', icon: Users },
};

const TypeBadge = ({ type }) => {
  const meta = TYPE_META[type] || { label: type, color: 'bg-slate-100 text-slate-800', icon: Users };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${meta.color}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
};

const CounterpartiesList = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const fetchRows = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('q', search);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      params.append('limit', '200');
      const r = await axios.get(`${API_URL}/api/counterparties?${params.toString()}`, auth);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  useEffect(() => {
    const t = setTimeout(() => fetchRows(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const r = await axios.post(`${API_URL}/api/counterparties`, {
        name: newName.trim(),
        types: [],
      }, auth);
      setCreating(false);
      setNewName('');
      navigate(`/admin/counterparties/${r.data.id}`);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="counterparties-list-page">
      <BackButton />

      <Card>
        <CardContent className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md" style={{ backgroundColor: '#023E8A15', color: '#023E8A' }}>
                <Users className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Master Counterparty Registry</h1>
                <p className="text-xs text-slate-500">{total} canonical entities across Suppliers, Clients, Partners, Leads, JV</p>
              </div>
            </div>
            <Button
              onClick={() => setCreating(true)}
              className="bg-[#FF6B35] hover:bg-[#E55A24] text-white"
              data-testid="counterparties-new-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              New Counterparty
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, legal name, license, tax, email…"
                className="pl-8 h-9"
                data-testid="counterparties-search"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="counterparties-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.keys(TYPE_META).map((t) => (
                  <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {creating && (
        <Card>
          <CardContent className="p-4 flex items-center gap-2">
            <Input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New counterparty name…"
              className="flex-1"
              data-testid="counterparties-new-name"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <Button onClick={handleCreate} className="bg-[#023E8A] text-white" data-testid="counterparties-new-submit">
              Create & Open
            </Button>
            <Button variant="outline" onClick={() => { setCreating(false); setNewName(''); }}>Cancel</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Name</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Legal name</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Types</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Country</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Tax / TRN</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600">Links</th>
                  <th className="px-3 py-2 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="py-8 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-slate-400">No counterparties yet</td></tr>
                )}
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/counterparties/${r.id}`)}
                    data-testid={`counterparty-row-${r.id}`}
                  >
                    <td className="px-3 py-2 font-medium text-slate-800 truncate">{r.name}</td>
                    <td className="px-3 py-2 text-slate-600 truncate">{r.legal_name || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(r.types || []).map((t) => <TypeBadge key={t} type={t} />)}
                        {(r.types || []).length === 0 && <span className="text-xs text-slate-400">—</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.country || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{r.tax_number || r.trade_license_number || '—'}</td>
                    <td className="px-3 py-2 text-center text-slate-500">{(r.cross_references || []).length}</td>
                    <td className="px-3 py-2">
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export { TypeBadge };
export default CounterpartiesList;
